/**
 * THE RESOLVER SEAM.
 *
 * The single point where the auth provider plugs into the @andavo/web-ui/api
 * `configureApi` fetch wrapper. The wrapper knows only two callbacks:
 *   getAccessToken() → bearer token (or undefined)
 *   clearCredentials() → called on a 401, drops the session
 *
 * Backed by MSAL/Entra (./msal.ts). At merge into andavo-dev the Entra→Auth0
 * swap replaces this file + msal.ts and nothing else — no component or
 * data-access hook imports MSAL directly.
 *
 * SCOPE NOTE: configureApi's default getAccessToken takes no scopes, so the
 * wrapper-routed (non-streaming) calls request the full configured scope set.
 * Modules whose backends need a SPECIFIC audience (chat → experts aud, CRM →
 * crm aud) and which BYPASS the wrapper (SSE/upload) call the scope-parameterized
 * acquireToken(scopes) directly — that is the auth_unification seam (phase 2/4).
 */
import { configureApi } from '@andavo/web-ui/api';
import { acquireToken, logout } from './msal';

// Vite injects this at build time; falls back to 'dev' locally. Sent as the
// X-Andavo-Client-Version header on every request by the wrapper.
const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? 'dev';

/**
 * Wire the auth provider into the API client. Call once at bootstrap, after
 * initMsal(). Mirrors experts-web's configureApiWithEntra.
 */
export function configureApiWithEntra(): void {
  configureApi({
    // Same-origin: the SPA is served alongside the backends via the edge (or the
    // Vite dev proxy), so paths like `v1/expense/reports` resolve to origin.
    baseUrl: '',
    appVersion: APP_VERSION,
    // Default (wrapper-routed) calls request the full configured scope set.
    // acquireToken's scopes param is optional → undefined means "all scopes".
    getAccessToken: () => acquireToken(),
    clearCredentials: () => {
      // A 401 means our token is no longer valid → drop the MSAL session.
      void logout();
    },
  });
}
